import { useState, type JSX } from "react";
import { toast } from "sonner";
import type { Task } from "@recipevault/shared";
import { Card, CardHeader, CardTitle, CardContent } from "@/ui/card.js";
import { friendlyError } from "../../shared/http.js";
import { useMe } from "../auth/useMe.js";
import { toggleTaskDone } from "./editWeek.js";
import { TaskForm } from "./TaskForm.js";

export interface TaskListProps {
  weekStart: string;
  tasks: Task[];
  /** Accessible name for the section landmark. PlanWeek renders two TaskLists (the
   * selected day's + the whole week's), so each needs a distinct name to avoid two
   * identically-named "Tasks" landmarks. Defaults to "Tasks". */
  label?: string;
}

/** Sorted by `doOnDate`, undone-first within a date (mirrors the shopping-list convention
 * of surfacing not-yet-done items first). */
function sortedTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.doOnDate !== b.doOnDate) return a.doOnDate < b.doOnDate ? -1 : 1;
    return Number(a.done) - Number(b.done);
  });
}

/**
 * Prep-task list (G4): the `TaskForm` fast path above a flat list of every task in
 * `plan.tasks`, each with a done checkbox. Toggling done goes through `toggleTaskDone`
 * (editWeek.js), which stamps `doneBy`/`doneAt` — reusing the same lazy-create pattern as
 * every other plan mutation.
 */
export function TaskList({ weekStart, tasks, label = "Tasks" }: TaskListProps): JSX.Element {
  const { me } = useMe();
  const userId = me?.user.userId ?? "";
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());

  async function handleToggle(taskId: string): Promise<void> {
    if (busyIds.has(taskId)) return;
    setBusyIds((s) => new Set(s).add(taskId));
    try {
      await toggleTaskDone(weekStart, taskId, userId);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusyIds((s) => {
        const n = new Set(s);
        n.delete(taskId);
        return n;
      });
    }
  }

  return (
    <section aria-label={label}>
      <Card>
        <CardHeader>
          <CardTitle>{label}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <TaskForm weekStart={weekStart} />

          {tasks.length > 0 && (
            <ul className="flex flex-col gap-1">
              {sortedTasks(tasks).map((task) => (
                <li key={task.id} className="flex items-center gap-3 rounded-md px-1 py-1.5">
                  <button
                    type="button"
                    aria-label={`Toggle ${task.title}`}
                    aria-pressed={task.done}
                    disabled={busyIds.has(task.id)}
                    onClick={() => {
                      void handleToggle(task.id);
                    }}
                    className="shrink-0"
                  >
                    <span
                      aria-hidden
                      className={`flex h-5 w-5 items-center justify-center rounded-sm border-2 transition-colors ${
                        task.done
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : "border-zinc-500 bg-transparent"
                      }`}
                    >
                      {task.done && (
                        <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden>
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  </button>

                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className={task.done ? "truncate text-zinc-500 line-through" : "truncate text-zinc-100"}>
                      {task.title}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {task.doOnDate}
                      {task.startByTime && ` · by ${task.startByTime}`}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
